import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Trophy, Star, Award, TrendingUp, Loader2, Zap, Target
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const container = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.04 } } };
const item = { hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } };

const LearnerGamificationPage = () => {
  const { user } = useAuth();
  const variant = user?.role === "manager" ? "manager" : "learner";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/my/gamification").then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <AdminLayout title="Gamification" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!data) return null;

  return (
    <AdminLayout title="Gamification" variant={variant}>
      <motion.div className="space-y-6" variants={container} initial="hidden" animate="show" data-testid="gamification-page">
        <motion.div variants={item}>
          <h1 className="text-2xl font-extrabold tracking-tight">Your Achievements</h1>
          <p className="text-sm text-muted-foreground mt-1">Track your points, level, and badges</p>
        </motion.div>

        {/* Level & Points */}
        <motion.div variants={item}>
          <Card className="border shadow-none bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-5 items-center">
                <div className="text-center">
                  <div className="w-20 h-20 mx-auto rounded-full bg-amber-100 flex items-center justify-center mb-2 border-4 border-amber-300">
                    <span className="text-3xl font-black text-amber-700">{data.level}</span>
                  </div>
                  <p className="text-xs font-bold text-amber-700">Level {data.level}</p>
                </div>
                <div className="sm:col-span-2">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-amber-700 font-semibold">{data.points} pts</span>
                    <span className="text-amber-600">Next level: {data.next_level_points} pts</span>
                  </div>
                  <Progress value={data.progress_to_next} className="h-3 [&>div]:bg-amber-500" />
                  <p className="text-[10px] text-amber-600 mt-1">{data.progress_to_next}% to Level {data.level + 1}</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-black text-amber-700">#{data.position}</p>
                  <p className="text-xs text-amber-600">of {data.total_participants} learners</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Stats */}
        <motion.div variants={item} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Points", value: data.points, icon: Star, color: "#f59e0b" },
            { label: "Level", value: data.level, icon: TrendingUp, color: "#8b5cf6" },
            { label: "Badges", value: data.badges?.length || 0, icon: Award, color: "#ec4899" },
            { label: "Rank", value: `#${data.position}`, icon: Trophy, color: "#3b82f6" },
          ].map((s, i) => (
            <Card key={i} className="border shadow-none"><CardContent className="p-3.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: s.color + "14" }}><s.icon className="w-4 h-4" style={{ color: s.color }} /></div>
              <div><p className="text-lg font-bold" style={{ color: s.color }}>{s.value}</p><p className="text-[10px] text-muted-foreground">{s.label}</p></div>
            </CardContent></Card>
          ))}
        </motion.div>

        {/* Badges */}
        <motion.div variants={item}>
          <Card className="border shadow-none">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm flex items-center gap-2"><Award className="w-4 h-4 text-primary" /> Earned Badges</CardTitle></CardHeader>
            <CardContent className="px-5 pb-5">
              {(data.badges || []).length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">Complete courses and assessments to earn badges</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {data.badges.map((b, i) => (
                    <div key={i} className="p-3 rounded-lg border text-center hover:shadow-sm transition-shadow" data-testid={`badge-${i}`}>
                      <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-2"><Zap className="w-5 h-5 text-primary" /></div>
                      <p className="text-xs font-bold">{b.badge_name || b.name || "Badge"}</p>
                      <p className="text-[10px] text-muted-foreground">{b.earned_at ? new Date(b.earned_at).toLocaleDateString() : ""}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Activity */}
        {data.recent_activity?.length > 0 && (
          <motion.div variants={item}>
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4 text-primary" /> Recent Activity</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5 space-y-2">
                {data.recent_activity.map((a, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg border text-xs">
                    <span className="text-slate-600">{a.description || a.action || "Points earned"}</span>
                    <Badge variant="outline" className="text-[10px]">+{a.points || 0} pts</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>
        )}
      </motion.div>
    </AdminLayout>
  );
};

export default LearnerGamificationPage;
